// ============================================================================
// TAB BAR B — THE GESTURE, WITHOUT THE DOM. docs/design/handoff-mobile-v10/boards/TabScrub.dc.html
// (behaviour "B · closes when you slide off"), imported by board 03.
//
//   · tap a tab → that tab
//   · press anywhere on the bar and drag → scrub: the highlight follows the finger, one buzz per
//     tab crossed, release selects
//   · tap +, hold + (380ms), or drag from the bar onto + → the capture menu, one buzz
//   · in the menu, slide onto an item → it lifts; release picks it
//   · THE B RULE: slide left of `cap.x − 44` or below `cap.y + 30` (off every item and off the
//     +) → the menu closes and scrubbing resumes under the same finger
//   · tap × (the rotated +) or tap outside → closes
//
// GEOMETRY IS MEASURED, NEVER THE BOARD'S 390PX FRAME. The host measures the bar, the five tabs
// and the + with getBoundingClientRect on every pointer-down and hands them in as a
// `BarGeometry`. The board's own distances (34, 44, 30, 72, 70 …) are LOCAL CSS px; `scale`
// (client px per local px, which is the app's Appearance → Size zoom) converts them, so the rule
// fires at the same physical place at 100% and at 130%.
//
// THIS CLASS NEVER RENDERS. It holds the drag in plain fields and tells the host about a change
// only when something visible changed — a tab crossed, the menu opened, an item lit — so a
// pointer move that crosses nothing costs no React render at all.
// ============================================================================

import { SHELL_SIZES } from './sizes'

export interface Pt {
  x: number
  y: number
}

export interface Box {
  left: number
  right: number
  top: number
  bottom: number
}

export interface BarGeometry {
  /** The five tabs, left to right, in client px. */
  tabs: readonly Box[]
  /** The capsule. */
  bar: Box
  /** The centre of the +. */
  cap: Pt
  /**
   * Client px per the bar's own CSS px. The bar counter-zooms the app's size setting, so this is
   * 1 in practice; it is still measured, so the rules hold if that ever changes.
   */
  scale: number
}

/**
 * Board 03's distances, in the bar's CSS px (multiplied by `scale` before use). The menu's
 * positions come from SHELL_SIZES (shell/sizes.ts), the same numbers index.css draws with.
 * The + keeps the board's 34px hit radius although it is drawn at 50px: a target a little larger
 * than its circle is kinder to a thumb, and the B rule's lines are measured from its centre.
 */
export const CAP_RADIUS = 34
export const CLOSE_LEFT = 44
export const CLOSE_DOWN = 30
export const OPEN_FROM_BAR = 30
export const ITEM_FIRST = SHELL_SIZES.menuFirst
export const ITEM_STEP = SHELL_SIZES.menuStep
/** Half the spacing: every point of the column belongs to exactly one item. */
export const ITEM_HIT = SHELL_SIZES.menuStep / 2
/** Left of `cap.x − 191` is off the menu's labels too (board: `p.x < 150` in a 390 frame, + at 341). */
export const ITEM_REACH = 191
export const MOVE_SLOP = 8
/** Hold + this long and the menu opens (board 03: 380ms). */
export const CAP_HOLD_MS = 380

export const MENU_ITEMS = ['voice', 'reminder', 'note', 'ask'] as const
export type MenuItem = (typeof MENU_ITEMS)[number]

/** The tab under `x`. Left of the first tab is the first; right of the last is the last. */
export function tabAtX(x: number, tabs: readonly Box[]): number {
  if (tabs.length === 0) return -1
  for (let i = 0; i < tabs.length; i++) if (x < tabs[i]!.right) return i
  return tabs.length - 1
}

export function onCapture(p: Pt, g: BarGeometry): boolean {
  const dx = p.x - g.cap.x
  const dy = p.y - g.cap.y
  const r = CAP_RADIUS * g.scale
  return dx * dx + dy * dy <= r * r
}

/** Item `i`'s centre: a single column above the +, Voice memo nearest. */
export function itemCentre(i: number, g: BarGeometry): Pt {
  return { x: g.cap.x, y: g.cap.y - (ITEM_FIRST + ITEM_STEP * i) * g.scale }
}

/** Which menu item the finger is on, or −1. The nearest centre within 34px, labels included. */
export function itemAt(p: Pt, g: BarGeometry, count: number = MENU_ITEMS.length): number {
  if (p.x < g.cap.x - ITEM_REACH * g.scale) return -1
  let best = -1
  let bestDistance = ITEM_HIT * g.scale
  for (let i = 0; i < count; i++) {
    const d = Math.abs(p.y - itemCentre(i, g).y)
    if (d < bestDistance) {
      bestDistance = d
      best = i
    }
  }
  return best
}

/** THE B RULE. Off every item, off the +, and either left of it or below it. */
export function slidOffMenu(p: Pt, g: BarGeometry, item: number): boolean {
  if (item >= 0 || onCapture(p, g)) return false
  return p.x < g.cap.x - CLOSE_LEFT * g.scale || p.y > g.cap.y + CLOSE_DOWN * g.scale
}

/** A scrub that reaches this far right has arrived on the + and opens the menu. */
export function reachesCapture(p: Pt, g: BarGeometry): boolean {
  return p.x > g.cap.x - OPEN_FROM_BAR * g.scale
}

export interface GestureView {
  /** The tab under the finger while scrubbing, or −1. */
  hover: number
  menu: boolean
  /** The lit menu item, or −1. */
  item: number
  /** The + is being pressed (it shrinks a little). */
  pressed: boolean
}

export const IDLE_VIEW: GestureView = { hover: -1, menu: false, item: -1, pressed: false }

export interface GestureHost {
  measure(): BarGeometry | null
  /** Called only when the view actually changed. */
  render(view: GestureView): void
  select(tab: number): void
  pick(item: MenuItem): void
  buzz(): void
  setTimer(fn: () => void, ms: number): unknown
  clearTimer(handle: unknown): void
}

type Mode = 'tab' | 'cap' | 'held' | 'closeTap' | 'pick'

interface Drag {
  mode: Mode
  moved: boolean
  x0: number
  y0: number
  geometry: BarGeometry
}

export class TabGesture {
  view: GestureView = IDLE_VIEW
  private drag: Drag | null = null
  private timer: unknown = null

  constructor(private readonly host: GestureHost) {}

  private set(patch: Partial<GestureView>): void {
    const next = { ...this.view, ...patch }
    const v = this.view
    if (next.hover === v.hover && next.menu === v.menu && next.item === v.item && next.pressed === v.pressed) {
      return
    }
    this.view = next
    this.host.render(next)
  }

  private clearHold(): void {
    if (this.timer !== null) this.host.clearTimer(this.timer)
    this.timer = null
  }

  private openMenu(): void {
    if (this.view.menu) return
    this.host.buzz()
    this.set({ menu: true, item: -1, hover: -1, pressed: false })
  }

  closeMenu(): void {
    this.clearHold()
    this.drag = null
    this.set({ menu: false, item: -1, pressed: false })
  }

  /** The keyboard's way in: Enter/Space on the + toggles the menu. */
  toggleMenu(): void {
    if (this.view.menu) this.closeMenu()
    else this.openMenu()
  }

  /** Returns true when the gesture is ours (the host then captures the pointer). */
  down(p: Pt): boolean {
    const geometry = this.host.measure()
    if (!geometry) return false
    this.clearHold()
    this.drag = { mode: 'tab', moved: false, x0: p.x, y0: p.y, geometry }

    if (this.view.menu) {
      if (onCapture(p, geometry)) {
        this.drag.mode = 'closeTap'
        this.set({ pressed: true })
        return true
      }
      const it = itemAt(p, geometry)
      if (it >= 0) {
        this.drag.mode = 'pick'
        this.host.buzz()
        this.set({ item: it })
        return true
      }
      // A tap outside the menu closes it, and that tap does nothing else.
      this.closeMenu()
      return false
    }

    if (onCapture(p, geometry)) {
      this.drag.mode = 'cap'
      this.set({ pressed: true })
      this.timer = this.host.setTimer(() => {
        this.timer = null
        if (this.drag) this.drag.mode = 'held'
        this.openMenu()
      }, CAP_HOLD_MS)
      return true
    }

    this.drag.mode = 'tab'
    this.host.buzz()
    this.set({ hover: tabAtX(p.x, geometry.tabs) })
    return true
  }

  move(p: Pt): void {
    const d = this.drag
    if (!d) return
    const g = d.geometry
    const dx = p.x - d.x0
    const dy = p.y - d.y0
    const slop = MOVE_SLOP * g.scale
    if (dx * dx + dy * dy > slop * slop) d.moved = true

    if (this.view.menu) {
      const it = itemAt(p, g)
      if (slidOffMenu(p, g, it)) {
        d.mode = 'tab'
        this.host.buzz()
        this.set({ menu: false, item: -1, pressed: false, hover: tabAtX(p.x, g.tabs) })
        return
      }
      if (d.mode === 'closeTap' && d.moved) {
        d.mode = 'pick'
        this.set({ pressed: false })
      }
      if (it !== this.view.item) {
        if (it >= 0) this.host.buzz()
        this.set({ item: it })
      }
      return
    }

    if (d.mode === 'cap' && d.moved) {
      this.clearHold()
      d.mode = 'held'
      this.openMenu()
      return
    }

    if (d.mode === 'tab') {
      if (reachesCapture(p, g)) {
        d.mode = 'held'
        this.openMenu()
        return
      }
      const t = tabAtX(p.x, g.tabs)
      if (t !== this.view.hover) {
        this.host.buzz()
        this.set({ hover: t })
      }
    }
  }

  up(p: Pt): void {
    this.clearHold()
    const d = this.drag
    if (!d) return
    this.drag = null

    if (d.mode === 'cap') {
      // A tap on + (released before the hold) opens the menu.
      this.openMenu()
      return
    }
    if (d.mode === 'closeTap') {
      this.closeMenu()
      return
    }
    if (this.view.menu) {
      const it = this.view.item
      if (it >= 0) {
        this.set({ menu: false, item: -1, pressed: false })
        this.host.pick(MENU_ITEMS[it]!)
      } else if (d.moved && !onCapture(p, d.geometry)) {
        this.closeMenu()
      } else {
        // Held open and released on the +: the menu stays, ready for a tap.
        this.set({ pressed: false })
      }
      return
    }
    if (d.mode === 'tab' && this.view.hover >= 0) {
      const tab = this.view.hover
      this.set({ hover: -1 })
      this.host.select(tab)
    }
  }

  /** The browser took the pointer (a system gesture, a scroll). Nothing is selected. */
  cancel(): void {
    this.clearHold()
    this.drag = null
    this.set({ hover: -1, pressed: false, item: -1 })
  }

  dispose(): void {
    this.clearHold()
    this.drag = null
  }
}
